import { useDeleteOrderLinesMutation } from "@saleor/graphql";
import getErrorOutDated from "@saleor/utils/errors/outDated";
import { FormikProps } from "formik";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { getIndexByItemNo, getItemByNo } from "../helpers";

interface Props {
  formikItemDetails: FormikProps<{ lines: NotRefOrderLine[] }>;
  selected: string[];
  isDescItemNo: boolean;
  clearSelected: () => void;
  setTableLoading: (loading: boolean) => void;
  isChangeFlow: boolean;
  setSubmitState?: Dispatch<SetStateAction<any>>;
  totalItemsOriginal?: number;
}

function useDeleteOrder({
  formikItemDetails,
  selected: deleteSelected,
  isDescItemNo,
  clearSelected,
  isChangeFlow,
  setTableLoading,
  setSubmitState,
  totalItemsOriginal
}: Props) {
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selection, setSelection] = React.useState<string[]>([]);
  // คิดว่าส่วนนี้ไม่น่าจะได้ใช้งานแล้ว (OrderLineDelete)
  const [isOrderLineDelete, setisOrderLineDelete] = useState({
    delete: false,
    setIsDeleteSuccess: null
  });
  const closeDeletePopup = () => setOpenDelete(false);
  const selected = isChangeFlow ? selection : deleteSelected;
  const handleOpenDelete = () => {
    setOpenDelete(true);
  };

  useEffect(() => {
    if (
      isOrderLineDelete?.delete &&
      isChangeFlow &&
      formikItemDetails?.values?.lines?.findIndex(
        (line: NotRefOrderLine) => line.isNewItem
      ) === -1
    ) {
      return isOrderLineDelete?.setIsDeleteSuccess(true);
    }
  }, [isOrderLineDelete?.delete]);
  const findChildSelected = (totalOrderLines, isDescItemNo) => {
    let childIndices = [];
    let index;
    const lines = [...formikItemDetails.values.lines];
    if (isDescItemNo) {
      lines.reverse();
    }
    for (const itemNo of selected) {
      index = getIndexByItemNo(lines, itemNo);
      let i = index + 1;
      if (lines[index]?.bomFlag && !lines[index]?.parentItemNo) {
        for (i; i < totalOrderLines; i++) {
          if (
            lines[i]?.parentItemNo &&
            lines[i]?.parentItemNo === lines[index]?.itemNo
          ) {
            childIndices.push(lines[i]?.itemNo);
          } else {
            break;
          }
        }
      }
    }
    childIndices = [...selected, ...childIndices];
    childIndices = childIndices.sort(
      (a, b) => parseInt(a, 10) - parseInt(b, 10)
    );

    return childIndices;
  };

  const updateParentItemNo = (updatedLines, isDescItemNo) => {
    let currentParentNo;
    let begin = isDescItemNo ? updatedLines.length - 1 : 0;
    const end = isDescItemNo ? -1 : updatedLines.length;
    const step = isDescItemNo ? -1 : 1;

    for (begin; begin !== end; begin += step) {
      const lineItem = updatedLines[begin];
      if (lineItem && lineItem.bomFlag && !lineItem.parentItemNo) {
        currentParentNo = lineItem.itemNo;
      }

      if (lineItem && lineItem.parentItemNo && currentParentNo) {
        if (lineItem.parentItemNo !== currentParentNo) {
          lineItem.parentItemNo = currentParentNo;
        }
      }
    }
  };

  const updateItemNos = (lines, childparentItemNos, totalOrderLines) => {
    let countAsc = 0;
    let countDesc = (totalOrderLines + 1 - childparentItemNos?.length) * 10;
    return lines
      .filter(line => !childparentItemNos.includes(line?.itemNo))
      .map(lineItem => ({
        ...lineItem,
        itemNo: String(isDescItemNo ? (countDesc -= 10) : (countAsc += 10))
      }));
  };
  const updateChangeOrderItemNos = (lines, childparentItemNos) => {
    const selectedItem = getItemByNo(lines, selected[0]);
    const copyLines = [...lines].filter(
      line => !childparentItemNos.includes(line?.itemNo)
    );
    const selectedIndex = getIndexByItemNo(
      copyLines,
      (parseInt(selectedItem?.itemNo, 10) - 10).toString()
    );
    let count = +selectedItem?.itemNo;
    for (
      let i = selectedIndex + (isDescItemNo ? -1 : +1);
      isDescItemNo ? i >= 0 : i < copyLines.length;
      isDescItemNo ? i-- : i++
    ) {
      if (copyLines[i].itemNo) {
        copyLines[i].itemNo = count;
        count += 10;
      }
    }
    return copyLines;
  };

  const [deleteNotRefOrderLines] = useDeleteOrderLinesMutation({
    onCompleted: data => {
      const lines = formikItemDetails?.values?.lines;
      const totalOrderLines = lines?.length;
      const childparentItemNos = findChildSelected(
        totalOrderLines,
        isDescItemNo
      );
      const errors = data?.deleteOrderLines?.errors;
      if (errors?.length === 0) {
        const updatedLines = isChangeFlow
          ? updateChangeOrderItemNos(lines, childparentItemNos)
          : updateItemNos(lines, childparentItemNos, totalOrderLines);
        updateParentItemNo(updatedLines, isDescItemNo);

        formikItemDetails.setFieldValue("lines", updatedLines);
        clearSelected();
        setisOrderLineDelete(prev => ({ ...prev, delete: true }));
      } else {
        if (
          selected.includes(lines[totalOrderLines - 1].itemNo) &&
          !lines[totalOrderLines - 1].materialCode
        ) {
          const updatedLines = lines.slice(0, -1);
          formikItemDetails.setFieldValue("lines", updatedLines);
          clearSelected();
          setisOrderLineDelete(prev => ({ ...prev, delete: true }));
        }
      }
      setisOrderLineDelete({ delete: false, setIsDeleteSuccess: null });
    }
  });

  const deleteOrderLinesInfo = (selectedItemNo: string[]) => {
    const orderLineIdMap = {};
    for (const item of formikItemDetails.values.lines) {
      orderLineIdMap[item?.itemNo] = item?.id;
    }

    let tempSelectedOrderLineIds: string[] = selectedItemNo.map(
      itemNo => orderLineIdMap[itemNo]
    );
    tempSelectedOrderLineIds = tempSelectedOrderLineIds.reverse();
    return tempSelectedOrderLineIds;
  };
  const deleteOrderLines = () => {
    setTableLoading(true);
    closeDeletePopup();
    deleteNotRefOrderLines({
      variables: {
        ids: deleteOrderLinesInfo(selected),
        totalItems: totalItemsOriginal
      }
    })
      .then(({ data }) => {
        const deleteOrderLines = data?.deleteOrderLines;
        const errorOutDated = getErrorOutDated(deleteOrderLines);
        if (errorOutDated) {
          setSubmitState({
            state: "errorPopup",
            context: errorOutDated
          });
        }
      })
      .finally(() => {
        setTableLoading(false);
      });
  };
  const deleteChangeOrderLines = (itemNo, setIsDeleteSuccess) => {
    setSelection([itemNo]);
    setisOrderLineDelete({
      delete: false,
      setIsDeleteSuccess
    });
    handleOpenDelete();
  };

  return {
    openDelete,
    handleOpenDelete,
    closeDeletePopup,
    deleteOrderLines,
    deleteChangeOrderLines
  };
}

export default useDeleteOrder;
